import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Users, MessageCircle, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import EnrollmentsPanel from "@/pages/admin/EnrollmentsPanel";
import TestimonialsPanel from "@/pages/admin/TestimonialsPanel";
import GalleryPanel from "@/pages/admin/GalleryPanel";

const TABS = [
  { id: "enrollments", label: "Enrollments", icon: Users },
  { id: "testimonials", label: "Testimonials", icon: MessageCircle },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
];

export default function AdminDashboard() {
  const { admin, loading, logout } = useAuth();
  const [tab, setTab] = useState("enrollments");
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <main className="min-h-screen bg-brand-cream" data-testid="admin-dashboard">
      <header className="border-b-[3px] border-brand-ink bg-white sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-brand-cream rounded-xl p-1 border-2 border-brand-ink">
              <Logo size={36} />
            </div>
            <div>
              <p className="font-display text-xl font-semibold text-brand-red leading-none">Cheeky Monkeys</p>
              <p className="text-xs text-brand-ink/60">Admin · {admin.email}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-white px-4 py-1.5 font-bold text-sm hover:bg-brand-yellow"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>

        <div className="mx-auto max-w-6xl px-6 pb-3 flex gap-2 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex items-center gap-2 rounded-full border-2 border-brand-ink px-4 py-1.5 font-bold text-sm shrink-0 transition-colors ${active ? "bg-brand-red text-white" : "bg-white text-brand-ink hover:bg-brand-yellow/60"}`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-8">
        {tab === "enrollments" && <EnrollmentsPanel />}
        {tab === "testimonials" && <TestimonialsPanel />}
        {tab === "gallery" && <GalleryPanel />}
      </section>
    </main>
  );
}
